// Trading Strategies: Strategy A (Contrarian) and Strategy B (Value Hunter)

import { 
  KalshiMarket, 
  Trade, 
  StrategyState,
  STRATEGY_A_PARAMS, 
  STRATEGY_B_PARAMS 
} from './types';
import { analyzeMarket } from './usl';

/**
 * Generate unique trade ID
 */
function generateTradeId(): string {
  return `trade_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Strategy A: The Contrarian
 * 
 * Filters:
 * - USL residual > 0.1 (market is overconfident)
 * - Volume between 100-1000
 * 
 * Action: Bet AGAINST the market lean
 * - If price < 50 (market leans NO), bet YES
 * - If price > 50 (market leans YES), bet NO
 */
export function qualifiesForStrategyA(market: KalshiMarket): boolean {
  // Check volume
  if (market.volume < STRATEGY_A_PARAMS.minVolume) return false;
  if (market.volume > STRATEGY_A_PARAMS.maxVolume) return false;
  
  // Check USL residual
  const analysis = analyzeMarket(market);
  if (analysis.uslResidual < STRATEGY_A_PARAMS.uslResidualMin) return false;
  
  // Must have valid price
  if (market.last_price <= 0 || market.last_price >= 100) return false;
  
  return true;
}

/**
 * Strategy B: The Value Hunter
 * 
 * Filters:
 * - Price between 20-40¢
 * - Volume between 100-1000
 * 
 * Action: Bet WITH the market lean
 * - If price < 50 (market leans NO), bet NO
 * - If price > 50 (market leans YES), bet YES
 */
export function qualifiesForStrategyB(market: KalshiMarket): boolean {
  // Check volume
  if (market.volume < STRATEGY_B_PARAMS.minVolume) return false;
  if (market.volume > STRATEGY_B_PARAMS.maxVolume) return false;
  
  // Check price range
  if (market.last_price < STRATEGY_B_PARAMS.minPrice) return false;
  if (market.last_price > STRATEGY_B_PARAMS.maxPrice) return false;
  
  return true;
}

/**
 * Determine bet direction for Strategy A (AGAINST market lean)
 */
export function getStrategyABetDirection(market: KalshiMarket): 'YES' | 'NO' {
  return market.last_price < 50 ? 'YES' : 'NO';
}

/**
 * Determine bet direction for Strategy B (WITH market lean)
 */
export function getStrategyBBetDirection(market: KalshiMarket): 'YES' | 'NO' {
  return market.last_price < 50 ? 'NO' : 'YES';
}

/**
 * Calculate cost to buy a position
 * 
 * @param price - Market price (0-100)
 * @param direction - YES or NO
 * @param betSize - Total bet size in dollars
 * @returns Cost in dollars
 */
export function calculateCost(
  price: number, 
  direction: 'YES' | 'NO', 
  betSize: number
): number {
  const costPerContract = direction === 'YES' ? price / 100 : (100 - price) / 100;
  return costPerContract * betSize;
}

/**
 * Calculate P&L for a resolved trade
 * 
 * @param trade - The trade
 * @param result - 'yes' or 'no' outcome
 * @returns P&L in dollars
 */
export function calculatePnL(trade: Trade, result: 'yes' | 'no'): number {
  const won = (trade.betDirection === 'YES' && result === 'yes') ||
              (trade.betDirection === 'NO' && result === 'no');
  
  if (won) {
    // Win: get back full bet size minus cost
    return trade.betSize - trade.cost;
  } else {
    // Loss: lose the cost
    return -trade.cost;
  }
}

/**
 * Create a trade for Strategy A
 */
export function createStrategyATrade(
  market: KalshiMarket,
  bankroll: number,
  betSizePercent: number
): Trade {
  const betDirection = getStrategyABetDirection(market);
  const betSize = bankroll * (betSizePercent / 100);
  const cost = calculateCost(market.last_price, betDirection, betSize);
  
  return {
    id: generateTradeId(),
    timestamp: new Date(),
    strategy: 'A',
    ticker: market.ticker,
    title: market.title,
    marketPrice: market.last_price,
    betDirection,
    betSize,
    cost,
    result: 'pending',
  };
}

/**
 * Create a trade for Strategy B
 */
export function createStrategyBTrade(
  market: KalshiMarket,
  bankroll: number,
  betSizePercent: number
): Trade {
  const betDirection = getStrategyBBetDirection(market);
  const betSize = bankroll * (betSizePercent / 100);
  const cost = calculateCost(market.last_price, betDirection, betSize);
  
  return {
    id: generateTradeId(),
    timestamp: new Date(),
    strategy: 'B',
    ticker: market.ticker,
    title: market.title,
    marketPrice: market.last_price,
    betDirection,
    betSize,
    cost,
    result: 'pending',
  };
}

/**
 * Filter markets for each strategy
 */
export function filterQualifyingMarkets(markets: KalshiMarket[]): {
  strategyA: KalshiMarket[];
  strategyB: KalshiMarket[];
} {
  return {
    strategyA: markets.filter(qualifiesForStrategyA),
    strategyB: markets.filter(qualifiesForStrategyB),
  };
}

/**
 * Initialize strategy state
 */
export function initializeStrategyState(
  name: string,
  description: string,
  initialBankroll: number,
  betSizePercent: number
): StrategyState {
  return {
    name,
    description,
    bankroll: initialBankroll,
    initialBankroll,
    betSizePercent,
    trades: [],
    isActive: false,
    totalPnL: 0,
    winRate: 0,
    totalTrades: 0,
    wins: 0,
    losses: 0,
  };
}

/**
 * Update strategy state after a trade resolves
 */
export function updateStrategyStateWithResult(
  state: StrategyState,
  tradeId: string,
  result: 'yes' | 'no'
): StrategyState {
  const tradeIndex = state.trades.findIndex(t => t.id === tradeId);
  if (tradeIndex === -1) return state;
  
  const trade = state.trades[tradeIndex];
  if (trade.result !== 'pending') return state; // Already resolved
  
  const pnl = calculatePnL(trade, result);
  const won = pnl > 0;
  
  // FIX: On win, add back full betSize. On loss, add nothing (cost already deducted)
  const bankrollChange = won ? trade.betSize : 0;
  
  const updatedTrade: Trade = {
    ...trade,
    result: won ? 'win' : 'loss',
    pnl,
    bankrollAfter: state.bankroll + bankrollChange,
  };
  
  const updatedTrades = [...state.trades];
  updatedTrades[tradeIndex] = updatedTrade;
  
  const newWins = state.wins + (won ? 1 : 0);
  const newLosses = state.losses + (won ? 0 : 1);
  const newTotalTrades = newWins + newLosses;
  
  return {
    ...state,
    bankroll: state.bankroll + bankrollChange,
    trades: updatedTrades,
    totalPnL: state.totalPnL + pnl,
    wins: newWins,
    losses: newLosses,
    totalTrades: newTotalTrades,
    winRate: newTotalTrades > 0 ? (newWins / newTotalTrades) * 100 : 0,
  };
}

/**
 * Add a new trade to strategy state
 */
export function addTradeToStrategy(state: StrategyState, trade: Trade): StrategyState {
  return {
    ...state,
    trades: [...state.trades, trade],
  };
}

/**
 * Get summary statistics for a strategy
 */
export function getStrategySummary(state: StrategyState): {
  returnPercent: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  maxDrawdown: number;
} {
  const resolvedTrades = state.trades.filter(t => t.result !== 'pending');
  
  if (resolvedTrades.length === 0) {
    return {
      returnPercent: 0,
      avgWin: 0,
      avgLoss: 0,
      profitFactor: 0,
      maxDrawdown: 0,
    };
  }
  
  const wins = resolvedTrades.filter(t => t.result === 'win');
  const losses = resolvedTrades.filter(t => t.result === 'loss');
  
  const totalWinAmount = wins.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const totalLossAmount = Math.abs(losses.reduce((sum, t) => sum + (t.pnl || 0), 0));
  
  // Calculate max drawdown
  let peak = state.initialBankroll;
  let maxDrawdown = 0;
  let runningBankroll = state.initialBankroll;
  
  for (const trade of resolvedTrades) {
    runningBankroll += trade.pnl || 0;
    if (runningBankroll > peak) {
      peak = runningBankroll;
    }
    const drawdown = (peak - runningBankroll) / peak;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  }
  
  return {
    returnPercent: ((state.bankroll - state.initialBankroll) / state.initialBankroll) * 100,
    avgWin: wins.length > 0 ? totalWinAmount / wins.length : 0,
    avgLoss: losses.length > 0 ? totalLossAmount / losses.length : 0,
    profitFactor: totalLossAmount > 0 ? totalWinAmount / totalLossAmount : totalWinAmount > 0 ? Infinity : 0,
    maxDrawdown: maxDrawdown * 100,
  };
}
